import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * ONE aggregated Ponesi Coins request for the authenticated user (`GET /api/me/loyalty`).
 * The homepage matches wallets to restaurant cards from this single response — never one
 * request per restaurant. Guests never trigger the call and have no wallet state.
 */
const LoyaltyContext = createContext({ wallets: [], byRestaurant: {}, loading: false, refresh: () => {} });

export const useLoyalty = () => useContext(LoyaltyContext);

export function LoyaltyProvider({ children }) {
  const { user } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setWallets([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/me/loyalty");
      setWallets(data?.wallets || []);
    } catch { /* keep the last canonical state; never invent a balance */ }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  useEffect(() => { refresh(); }, [refresh]);

  const byRestaurant = useMemo(
    () => Object.fromEntries(wallets.map((w) => [w.restaurant_id, w])), [wallets]);

  return (
    <LoyaltyContext.Provider value={{ wallets, byRestaurant, loading, refresh }}>
      {children}
    </LoyaltyContext.Provider>
  );
}
